import { useState } from "react";
import { Inbox, TriangleAlert } from "lucide-react";

import {
  type CarrierRefundActionEvent,
  type CarrierRefundLine,
  useCarrierRefundActionEvents,
  useCarrierRefundLines,
} from "@/api/carrier";
import { TablePager, usePagedState } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ApproveRefundLineSheet,
  type RefundLineApproval,
} from "@/features/approve-refund-line-sheet";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDate, formatMoney } from "@/lib/format";

function formatCharge(value: string): string {
  return value.replace(/_/g, " ").toLowerCase();
}

function personName(person: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}): string {
  return (
    [person.firstName, person.lastName].filter(Boolean).join(" ") ||
    person.email
  );
}

function requesterLabel(line: CarrierRefundLine): string {
  const customer = line.refundRequest.requestedByCustomer;
  if (customer) return `Customer · ${personName(customer)}`;
  const staff = line.refundRequest.requestedByInternalUser;
  if (staff) return `EZX staff · ${personName(staff)}`;
  return "Automated system";
}

function actorLabel(event: CarrierRefundActionEvent): string {
  if (event.actorCarrier) {
    return event.actorCarrier.name || event.actorCarrier.email;
  }
  if (event.actorInternal) {
    return (
      [event.actorInternal.firstName, event.actorInternal.lastName]
        .filter(Boolean)
        .join(" ") || event.actorInternal.email
    );
  }
  return "EZX";
}

function eventDecision(event: CarrierRefundActionEvent): string | null {
  const decision = event.details?.decision;
  return typeof decision === "string" ? decision : null;
}

function approvalFor(line: CarrierRefundLine): RefundLineApproval {
  return {
    refundRequestId: line.refundRequest.id,
    lineId: line.id,
    shipmentId: line.shipment?.id ?? null,
    shipmentLabel: line.shipment
      ? `SHP-${line.shipment.shipmentNumber}`
      : "No shipment",
    chargeType: line.chargeType,
    amountNetCents: line.amountNetCents,
    taxCents: line.taxCents,
    amountGrossCents: line.amountGrossCents,
    createdAt: line.createdAt,
    requestedAt: line.refundRequest.requestedAt,
    source: line.refundRequest.source,
    reason: line.refundRequest.reason,
    requester: line.refundRequest.requestedByCustomer
      ? {
          type: "Customer",
          name: personName(line.refundRequest.requestedByCustomer),
          email: line.refundRequest.requestedByCustomer.email,
          phone: line.refundRequest.requestedByCustomer.phone,
          customerNumber: line.refundRequest.requestedByCustomer.customerNumber,
        }
      : line.refundRequest.requestedByInternalUser
        ? {
            type: "EZX staff",
            name: personName(line.refundRequest.requestedByInternalUser),
            email: line.refundRequest.requestedByInternalUser.email,
            phone: line.refundRequest.requestedByInternalUser.phone,
            customerNumber: null,
          }
        : {
            type: "Automated system",
            name: null,
            email: null,
            phone: null,
            customerNumber: null,
          },
    customer: line.shipment?.order?.placerCustomer
      ? {
          name: personName(line.shipment.order.placerCustomer),
          email: line.shipment.order.placerCustomer.email,
          phone: line.shipment.order.placerCustomer.phone,
          customerNumber: line.shipment.order.placerCustomer.customerNumber,
        }
      : line.refundRequest.requestedByCustomer
        ? {
            name: personName(line.refundRequest.requestedByCustomer),
            email: line.refundRequest.requestedByCustomer.email,
            phone: line.refundRequest.requestedByCustomer.phone,
            customerNumber:
              line.refundRequest.requestedByCustomer.customerNumber,
          }
        : null,
  };
}

function QueueMessage({
  kind,
  onRetry,
}: {
  kind: "empty" | "error" | "forbidden";
  onRetry: () => void;
}) {
  const message =
    kind === "forbidden"
      ? "You do not have permission to view refund approvals."
      : kind === "error"
        ? "Refund approvals could not be loaded."
        : "No refunds awaiting your approval.";
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
      {kind === "empty" ? (
        <Inbox className="h-7 w-7 text-muted-foreground" />
      ) : (
        <TriangleAlert className="h-7 w-7 text-destructive" />
      )}
      <p role={kind === "empty" ? undefined : "alert"} className="text-sm">
        {message}
      </p>
      {kind === "error" && (
        <Button type="button" variant="outline" size="touch" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function RefundsPanel() {
  const { page, pageSize, setPage, setPageSize } = usePagedState();
  const offset = (page - 1) * pageSize;
  const { data, isLoading, error, refetch } = useCarrierRefundLines({
    limit: pageSize,
    offset,
  });
  const actionEvents = useCarrierRefundActionEvents({
    actionNeeded: true,
    limit: 100,
  });
  const outcomeEvents = useCarrierRefundActionEvents({
    actionNeeded: false,
    limit: 20,
  });
  const [approval, setApproval] = useState<RefundLineApproval | null>(null);

  const rows = Array.isArray(data) ? data : (data?.data ?? []);
  const pagination = Array.isArray(data) ? null : data?.pagination;
  const blockingError = error && rows.length === 0;
  const reminders = (actionEvents.data ?? []).filter(
    (event) => event.type === "REMINDER"
  );
  const assignments = (actionEvents.data ?? []).filter(
    (event) => event.type === "ASSIGNED"
  );
  const outcomes = (outcomeEvents.data ?? [])
    .filter((event) => event.refundLine)
    .filter(
      (event, index, events) =>
        events.findIndex(
          (candidate) => candidate.refundLine?.id === event.refundLine?.id
        ) === index
    )
    .slice(0, 5);

  return (
    <div className="space-y-4">
      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Provider refund approvals</h2>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
              Authorize only carrier-owned service components. EZX performs the
              customer settlement and records settled provider amounts as
              carrier payable adjustments. Amounts below are your provider
              component, not the customer’s total refund.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-900">
              {assignments.length} assigned
            </span>
            <span className="rounded-full bg-red-100 px-2.5 py-1 font-medium text-red-900">
              {reminders.length} reminders
            </span>
          </div>
        </div>

        {reminders.length > 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
            <p className="font-medium">Reminder · Action required</p>
            <p className="mt-1 text-xs">
              Reminder after 2 business days; unresolved provider work escalates
              to EZX after 5 business days.
            </p>
          </div>
        ) : assignments.length > 0 ? (
          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            <p className="font-medium">Assigned · Action required</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Review each carrier-owned component before its escalation date.
            </p>
          </div>
        ) : null}

        {(actionEvents.error || outcomeEvents.error) && (
          <p role="alert" className="text-xs text-amber-800">
            Assignment or decision history could not be refreshed. The approval
            queue remains available.
          </p>
        )}

        {outcomes.length > 0 && (
          <div className="border-t pt-3">
            <h3 className="text-sm font-medium">Recent decision history</h3>
            <ul className="mt-2 space-y-2">
              {outcomes.map((event) => {
                const authorized =
                  event.type === "RESOLVED" &&
                  eventDecision(event) === "approve";
                const disputed = event.type === "DISPUTED_TO_ADMIN";
                const timestamp = event.resolvedAt ?? event.createdAt;
                return (
                  <li
                    key={event.id}
                    className="flex flex-wrap items-start justify-between gap-2 rounded-md border p-2.5 text-xs"
                  >
                    <div>
                      <p className="font-medium">
                        {authorized
                          ? `Authorized by ${actorLabel(event)}`
                          : disputed
                            ? `Disputed by ${actorLabel(event)}`
                            : "Escalated to EZX review"}
                      </p>
                      <p className="mt-0.5 text-muted-foreground">
                        {formatCharge(
                          event.refundLine?.chargeType ?? "component"
                        )}{" "}
                        · {formatMoney(event.refundLine?.amountGrossCents ?? 0)}
                      </p>
                      <p className="mt-0.5 text-muted-foreground">
                        {event.refundRequest.reason ?? "No reason provided"}
                      </p>
                    </div>
                    <div className="text-right text-muted-foreground">
                      <p>{formatDate(timestamp)}</p>
                      <p className="mt-0.5">
                        {authorized
                          ? "Authorization recorded · EZX handles settlement"
                          : "EZX review pending"}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        {error && rows.length > 0 && (
          <div
            role="alert"
            className="border-b border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
          >
            Refresh failed. Loaded refund approvals remain available.
          </div>
        )}

        {isLoading && rows.length === 0 ? (
          <div
            role="status"
            aria-label="Loading refund approvals"
            className="space-y-2 p-3"
          >
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </div>
        ) : blockingError ? (
          <QueueMessage
            kind={hasHttpStatus(error, 403) ? "forbidden" : "error"}
            onRetry={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <QueueMessage kind="empty" onRetry={() => void refetch()} />
        ) : (
          <>
            <div className="space-y-2 p-3 md:hidden">
              {rows.map((line) => (
                <article
                  key={line.id}
                  className="relative rounded-md border p-3"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label={`Review refund for ${approvalFor(line).shipmentLabel}`}
                    className="absolute inset-0 z-0 h-full w-full rounded-md p-0"
                    onClick={() => setApproval(approvalFor(line))}
                  />
                  <div className="pointer-events-none relative z-10 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-mono text-xs font-semibold">
                        {line.shipment
                          ? `SHP-${line.shipment.shipmentNumber}`
                          : "No shipment"}
                      </h3>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {formatCharge(line.chargeType)}
                      </p>
                    </div>
                    <StatusBadge status="PENDING" />
                  </div>
                  <p className="pointer-events-none relative z-10 mt-2 line-clamp-2 break-words text-xs">
                    {line.refundRequest.reason ?? "No reason provided"}
                  </p>
                  <p className="pointer-events-none relative z-10 mt-1 truncate text-[11px] text-muted-foreground">
                    {requesterLabel(line)}
                  </p>
                  <div className="pointer-events-none relative z-10 mt-2 flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
                    <span>{formatDate(line.refundRequest.requestedAt)}</span>
                    <span className="font-semibold text-foreground">
                      Provider component · {formatMoney(line.amountGrossCents)}{" "}
                      CAD
                    </span>
                  </div>
                </article>
              ))}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Shipment</TableHead>
                    <TableHead>Charge</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead className="text-right">
                      Provider component
                    </TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="font-mono text-xs">
                        <div>
                          {line.shipment
                            ? `SHP-${line.shipment.shipmentNumber}`
                            : "-"}
                        </div>
                        <div className="max-w-48 truncate font-sans text-[11px] text-muted-foreground">
                          {requesterLabel(line)}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatCharge(line.chargeType)}
                      </TableCell>
                      <TableCell className="max-w-64 truncate text-xs">
                        {line.refundRequest.reason ?? "No reason provided"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(line.refundRequest.requestedAt)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        {formatMoney(line.amountGrossCents)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          aria-label={`Review refund details for ${approvalFor(line).shipmentLabel}`}
                          onClick={() => setApproval(approvalFor(line))}
                        >
                          Review
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {pagination && (
          <div className="border-t p-3">
            <TablePager
              page={Math.floor(pagination.offset / pagination.limit) + 1}
              pageSize={pagination.limit}
              total={pagination.total}
              totalPages={Math.max(
                1,
                Math.ceil(pagination.total / pagination.limit)
              )}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      <ApproveRefundLineSheet
        key={approval?.lineId ?? "closed"}
        approval={approval}
        onOpenChange={(open) => {
          if (!open) setApproval(null);
        }}
      />
    </div>
  );
}
